'use client';

import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import { getServiceMetrics } from '@/lib/api';
import type { ServiceMetrics } from '@/types';

function formatRate(value: number | undefined) {
  if (value === undefined) {
    return '—';
  }
  const perMinute = Math.round(value * 60);
  return perMinute.toLocaleString();
}

function MetricChip({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex items-center gap-1 rounded-base border-2 border-border bg-background h-12 px-3">
      <span className="text-[10px] text-foreground/60 font-base">{label}</span>
      <span className="text-sm font-heading text-foreground tabular-nums">{formatRate(value)}</span>
      <span className="text-[10px] text-foreground/60 font-base">/min</span>
    </div>
  );
}

export function LiveThroughputBar() {
  const [metrics, setMetrics] = useState<ServiceMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchMetrics = async () => {
      try {
        const data = await getServiceMetrics();
        if (mounted) {
          setMetrics(data);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
        }
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const statusLabel = error ? 'Snapshot unavailable' : 'Frozen snapshot';
  const statusColor = error ? 'bg-red-500' : 'bg-yellow-500';
  const ingestRate = metrics?.ingest_rate;
  const drayRate = metrics?.dray_rate;

  return (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      <div className="flex items-center gap-2 h-12">
        <Activity className="w-4 h-4 text-main" />
        <span className="text-xs font-heading text-foreground">Throughput</span>
        <span className="flex items-center gap-1 text-[10px] text-foreground/60 font-base">
          <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
          {statusLabel}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <MetricChip label="Ingest" value={ingestRate} />
        <MetricChip label="Dray" value={drayRate} />
      </div>
    </div>
  );
}
